import React, { useMemo, useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import styles from './PiecePalette.module.css';
import { allPieceTypes } from '../../services/zodiacEngine/const';
import { PieceType } from '../../services/zodiacEngine/types';

interface PiecePaletteProps {
  onDragStart?: (event: React.DragEvent<HTMLImageElement>, piece: PieceType, isLight: boolean) => void;
  onBoardPieceDroppedToSandbox?: (pieceType: PieceType, isLight: boolean, fromRow: number, fromCol: number, pieceId: string) => void;
}

export interface PiecePaletteRef {
  handlePiecePlaced: (pieceKey: string) => void;
  handlePieceDropFailed: (pieceKey: string) => void;
  returnCapturedPiece: (pieceType: PieceType, isLight: boolean) => void;
  isPieceOnBoard: (pieceType: PieceType, isLight: boolean) => boolean;
  getSelectedPiece: () => {type: PieceType, isLight: boolean} | null;
  clearSelectedPiece: () => void;
  tidyPieces: () => void;
  markBoardPiecesAsPlaced: (pieces: Array<{type: PieceType, isLight: boolean}>) => void;
  resetAllPlaced: () => void;
}

const PiecePalette = forwardRef<PiecePaletteRef, PiecePaletteProps>(({ onDragStart, onBoardPieceDroppedToSandbox }, ref) => {
  // Track which pieces have been placed on the board
  const [placedPieces, setPlacedPieces] = useState<Set<string>>(new Set());
  // Track currently dragging piece for manual drag
  const [draggingPiece, setDraggingPiece] = useState<string | null>(null);
  // Track which piece the gem is hiding under
  const [gemHostPieceKey, setGemHostPieceKey] = useState<string | null>(null);
  // Track gem's initial position (frozen at placement time)
  const [gemInitialPosition, setGemInitialPosition] = useState<{top: string, left: string} | null>(null);
  // Track gem discovery state
  const [gemDiscovered, setGemDiscovered] = useState<boolean>(false);
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  // Track the audio instance for stopping music
  const [gemAudio, setGemAudio] = useState<HTMLAudioElement | null>(null);
  // Store original positions for snap-back
  const [dragStartPositions, setDragStartPositions] = useState<Map<string, {top: string, left: string}>>(new Map());
  // Store dynamic positions that can be updated by repositioning
  const [dynamicPositions, setDynamicPositions] = useState<Map<string, {top: string, left: string}>>(() => {
    // Load saved positions from localStorage on mount
    const saved = localStorage.getItem('sandboxPiecePositions');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return new Map(Object.entries(parsed));
      } catch (e) {
      }
    }
    return new Map();
  });
  // Track mouse position during manual drag
  const [dragOffset, setDragOffset] = useState<{x: number, y: number} | null>(null);
  // Mobile-specific state
  const [activeColorTab, setActiveColorTab] = useState<'light' | 'dark'>('light');
  const [selectedPiece, setSelectedPiece] = useState<{type: PieceType, isLight: boolean} | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Save dynamic positions to localStorage whenever they change
  useEffect(() => {
    if (dynamicPositions.size > 0) {
      const positionsObj = Object.fromEntries(dynamicPositions);
      localStorage.setItem('sandboxPiecePositions', JSON.stringify(positionsObj));
    }
  }, [dynamicPositions]);

  // Detect if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mouse-based drag handlers (HTML5 drag is broken)
  const handleMouseDown = (
    event: React.MouseEvent<HTMLImageElement>,
    piece: PieceType,
    isLight: boolean,
    pieceKey: string,
    currentPosition: {top: string, left: string}
  ) => {
    event.preventDefault();
    
    setDraggingPiece(pieceKey);
    setDragStartPositions(prev => new Map(prev).set(pieceKey, currentPosition));
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingPiece) return;
    
    // Calculate new position relative to container
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left - 25; // Center piece on cursor
    const mouseY = event.clientY - rect.top - 25;
    
    const leftPercent = (mouseX / rect.width) * 100;
    const topPercent = (mouseY / rect.height) * 100;
    
    // Clamp to sandbox boundaries
    const clampedLeft = Math.max(SAND_BOUNDARIES.leftMin, Math.min(SAND_BOUNDARIES.leftMax, leftPercent));
    const clampedTop = Math.max(SAND_BOUNDARIES.topMin, Math.min(SAND_BOUNDARIES.topMax, topPercent));
    
    const newPosition = {
      left: `${clampedLeft}%`,
      top: `${clampedTop}%`
    };
    
    // Update position in real-time
    setDynamicPositions(prev => new Map(prev).set(draggingPiece, newPosition));
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggingPiece) {
      setDraggingPiece(null);
    }
  };

  // Handle drops within the sandbox for repositioning
  const handleSandboxDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const pieceData = event.dataTransfer.getData("piece");
    if (!pieceData) return;
    
    const parsedData = JSON.parse(pieceData);
    const { sandboxKey, isRepositioning, isFromBoard, type, isLight, fromRow, fromCol, pieceId } = parsedData;

    // Handle board piece dropped back to sandbox
    if (isFromBoard && onBoardPieceDroppedToSandbox) {
      // Calculate drop position within the sandbox
      const rect = event.currentTarget.getBoundingClientRect();
      const dropX = event.clientX - rect.left - 25;
      const dropY = event.clientY - rect.top - 25;
      const leftPercent = Math.max(SAND_BOUNDARIES.leftMin, Math.min(SAND_BOUNDARIES.leftMax, (dropX / rect.width) * 100));
      const topPercent = Math.max(SAND_BOUNDARIES.topMin, Math.min(SAND_BOUNDARIES.topMax, (dropY / rect.height) * 100));
      const dropPosition = { left: `${leftPercent}%`, top: `${topPercent}%` };

      // Remove piece from board
      onBoardPieceDroppedToSandbox(type, isLight, fromRow, fromCol, pieceId);

      // Return piece to sandbox at the drop position
      const suffix = isLight ? 'light' : 'dark';
      const pieceKey = Array.from(placedPieces).find(key =>
        key.startsWith(`${type}-${suffix}`)
      );
      if (pieceKey) {
        setPlacedPieces(prev => {
          const newSet = new Set(prev);
          newSet.delete(pieceKey);
          return newSet;
        });
        setDynamicPositions(prev => new Map(prev).set(pieceKey, dropPosition));
      }
      return;
    }

    // Only handle repositioning drops for palette pieces
    if (!isRepositioning || !sandboxKey) return;
    
    
    // Calculate new position based on drop location
    const rect = event.currentTarget.getBoundingClientRect();
    const dropX = event.clientX - rect.left - 25; // Center piece
    const dropY = event.clientY - rect.top - 25;
    
    const leftPercent = (dropX / rect.width) * 100;
    const topPercent = (dropY / rect.height) * 100;
    
    // Check if within sandbox bounds
    const isWithinBounds = 
      leftPercent >= SAND_BOUNDARIES.leftMin && leftPercent <= SAND_BOUNDARIES.leftMax &&
      topPercent >= SAND_BOUNDARIES.topMin && topPercent <= SAND_BOUNDARIES.topMax;
    
    if (!isWithinBounds) {
      return;
    }
    
    const clampedLeft = Math.max(SAND_BOUNDARIES.leftMin, Math.min(SAND_BOUNDARIES.leftMax, leftPercent));
    const clampedTop = Math.max(SAND_BOUNDARIES.topMin, Math.min(SAND_BOUNDARIES.topMax, topPercent));
    
    const newPosition = {
      left: `${clampedLeft}%`,
      top: `${clampedTop}%`
    };
    
    // Update the piece position in the sandbox
    setDynamicPositions(prev => new Map(prev).set(sandboxKey, newPosition));
    
    // Play soft sand landing sound
    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.3; // Soft volume for sand sound
    audio.play().catch(() => {}); // Silently handle any errors
    
  };

  const handleSandboxDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    
    // Don't stop propagation to allow parent components to also handle
    // event.stopPropagation();
    
    event.dataTransfer.dropEffect = "move";
  };

  // Function to be called by parent when piece is successfully placed
  const handlePiecePlaced = (pieceKey: string) => {
    setPlacedPieces(prev => new Set(prev).add(pieceKey));
    setDraggingPiece(null);
  };

  // Function to be called by parent when piece drop fails
  const handlePieceDropFailed = (pieceKey: string) => {
    setDraggingPiece(null);
    // Piece stays in sandbox (no state change needed)
  };

  // Function to return a captured piece to the sandbox at a random location
  const returnCapturedPiece = (pieceType: PieceType, isLight: boolean) => {
    // Find the piece key that was placed on the board
    const suffix = isLight ? 'light' : 'dark';
    const pieceKey = Array.from(placedPieces).find(key =>
      key.startsWith(`${pieceType}-${suffix}`)
    );

    if (pieceKey) {
      // Remove it from placed pieces (return it to sandbox)
      setPlacedPieces(prev => {
        const newSet = new Set(prev);
        newSet.delete(pieceKey);
        return newSet;
      });

      // Place piece at a random position in the sandbox
      const randomPosition = generateRandomPosition();
      setDynamicPositions(prev => new Map(prev).set(pieceKey, randomPosition));

      // Play sand sound when piece is thrown back into sandbox
      const audio = new Audio('/sand1.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {}); // Silently handle any errors
    }
  };

  // Function to check if a piece type and color is currently on the board
  const isPieceOnBoard = (pieceType: PieceType, isLight: boolean): boolean => {
    const suffix = isLight ? 'light' : 'dark';
    return Array.from(placedPieces).some(key => 
      key.startsWith(`${pieceType}-${suffix}`)
    );
  };

  // HTML5 drag handlers
  const handleDragStart = (
    event: React.DragEvent<HTMLImageElement>,
    piece: PieceType,
    isLight: boolean,
    pieceKey: string,
    currentPosition: {top: string, left: string}
  ) => {

    // Force grabbing cursor during drag
    document.body.style.cursor = 'grabbing';
    document.body.style.setProperty('cursor', 'grabbing', 'important');
    
    // Store piece data for drop handling - use same format as working PieceDrag component
    const pieceData = {
      type: piece,  // Use 'type' instead of 'piece' to match working implementation
      isLight,
      sandboxKey: pieceKey,
      isRepositioning: true // This is repositioning within sandbox
    };
    
    event.dataTransfer.setData("piece", JSON.stringify(pieceData));
    event.dataTransfer.effectAllowed = "move";
    
    // Custom drag image using offscreen DOM element (matches board piece drag)
    const element = event.currentTarget;
    const size = Math.round(Math.max(element.offsetWidth, element.offsetHeight) * 1.6);
    const dragEl = document.createElement('div');
    dragEl.style.position = 'fixed';
    dragEl.style.top = '-1000px';
    dragEl.style.left = '-1000px';
    dragEl.style.width = `${size}px`;
    dragEl.style.height = `${size}px`;
    dragEl.style.pointerEvents = 'none';
    dragEl.style.zIndex = '-1';

    const img = document.createElement('img');
    img.src = element.src;
    img.style.width = `${size}px`;
    img.style.height = `${size}px`;
    dragEl.appendChild(img);
    document.body.appendChild(dragEl);

    event.dataTransfer.setDragImage(dragEl, size / 2, size / 2);

    requestAnimationFrame(() => {
      document.body.removeChild(dragEl);
    });

    // Hide the original element during drag
    element.style.opacity = '0';
    
    // Store original position for snap-back
    setDragStartPositions(prev => new Map(prev).set(pieceKey, currentPosition));
    
    // Call parent's onDragStart if provided
    if (onDragStart) {
      onDragStart(event, piece, isLight);
    }
  };

  const handleDragEnd = (event: React.DragEvent<HTMLImageElement>) => {

    // Revert original element styles after drag
    const element = event.currentTarget;
    element.style.opacity = '1';
    element.style.filter = '';
    element.style.transform = '';

    // Reset cursor back to default
    document.body.style.cursor = '';
    document.body.style.removeProperty('cursor');

    // Drag end is handled by parent components through the ref methods
  };


  // Configurable boundaries for sand pit area - adjust these values to fine-tune positioning
  const SAND_BOUNDARIES = {
    topMin: 32,      // Minimum top position (%)
    topMax: 95,      // Maximum top position (%)
    leftMin: 35,     // Minimum left position (%)
    leftMax: 96,     // Maximum left position (%)
  };

  // Generate random positions within the wooden sand pit boundaries
  const generateRandomPosition = () => ({
    top: `${Math.random() * (SAND_BOUNDARIES.topMax - SAND_BOUNDARIES.topMin) + SAND_BOUNDARIES.topMin}%`,
    left: `${Math.random() * (SAND_BOUNDARIES.leftMax - SAND_BOUNDARIES.leftMin) + SAND_BOUNDARIES.leftMin}%`
  });

  // Create organized positions for all 26 pieces (13 light + 13 dark) alphabetically
  const piecePositions = useMemo(() => {
    // allPieceTypes order: Bear, Crane, Frog, Fungi, Goat, Monkey, Octopus, Penguin, Rhino, Snake, Tree, Virus, Wolf
    // Already in alphabetical order!
    // Light pieces (indices 0-12) on top three rows
    // Dark pieces (indices 13-25) on bottom three rows

    const positions: Array<{top: string, left: string}> = new Array(26);

    // Layout configuration - 6 rows total (3 for light, 3 for dark)
    const startTop = SAND_BOUNDARIES.topMin + 3;
    const rowHeight = (SAND_BOUNDARIES.topMax - startTop - 3) / 6;
    const startLeft = SAND_BOUNDARIES.leftMin + 5;
    const colWidth = (SAND_BOUNDARIES.leftMax - startLeft - 5) / 5; // Max 5 pieces per row

    // Dark pieces - top 3 rows (indices 13-25)
    // Row 0: 5 pieces (13-17)
    // Row 1: 5 pieces (18-22)
    // Row 2: 3 pieces (23-25)
    for (let i = 0; i < 13; i++) {
      const row = i < 5 ? 0 : i < 10 ? 1 : 2;
      const col = i < 5 ? i : i < 10 ? i - 5 : i - 10;
      const top = startTop + (row * rowHeight) + rowHeight / 2;
      const left = startLeft + (col * colWidth) + colWidth / 2;
      positions[i + 13] = { top: `${top}%`, left: `${left}%` };
    }

    // Light pieces - bottom 3 rows (indices 0-12)
    // Row 3: 5 pieces (0-4)
    // Row 4: 5 pieces (5-9)
    // Row 5: 3 pieces (10-12)
    for (let i = 0; i < 13; i++) {
      const row = i < 5 ? 3 : i < 10 ? 4 : 5;
      const col = i < 5 ? i : i < 10 ? i - 5 : i - 10;
      const top = startTop + (row * rowHeight) + rowHeight / 2;
      const left = startLeft + (col * colWidth) + colWidth / 2;
      positions[i] = { top: `${top}%`, left: `${left}%` };
    }

    return positions;
  }, []); // Empty dependency array means this only runs once when component mounts

  // Select a random piece to hide the gem under on component mount
  useEffect(() => {
    if (gemHostPieceKey) return; // Already selected

    const randomPieceIndex = Math.floor(Math.random() * 26); // 0-25 for all pieces
    const allPieces: string[] = [];

    // Generate all piece keys in the same order as renderAllPieces
    allPieceTypes.forEach((piece, index) => {
      allPieces.push(`${piece}-light-${index}`);
    });
    allPieceTypes.forEach((piece, index) => {
      allPieces.push(`${piece}-dark-${index + 13}`);
    });

    const selectedPieceKey = allPieces[randomPieceIndex];

    // Store the gem's initial position (frozen at this point)
    const initialPosition = piecePositions[randomPieceIndex];
    setGemInitialPosition(initialPosition);
    setGemHostPieceKey(selectedPieceKey);
  }, [gemHostPieceKey, piecePositions]); // Depends on piecePositions being ready

  // Handle gem discovery click
  const handleGemClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setGemDiscovered(true);
    setShowCelebration(true);

    // Play the gem discovery song with seamless looping
    const audio = new Audio('/Grimes - World Princess Part II [Official Video].mp3');
    audio.volume = 0.5; // Adjust volume as needed
    setGemAudio(audio); // Store audio instance for later stopping

    // Seamless loop handler - named function to allow recursion
    const setupSeamlessLoop = (audioInstance: HTMLAudioElement) => {
      let hasTriggeredNext = false; // Prevent multiple triggers

      const handleTimeUpdate = () => {
        const timeLeft = audioInstance.duration - audioInstance.currentTime;

        // When 0.15 seconds left, start new instance at 1.7 seconds
        if (timeLeft <= 0.15 && timeLeft > 0.10 && !hasTriggeredNext) {
          hasTriggeredNext = true; // Prevent multiple triggers

          const newAudio = new Audio('/Grimes - World Princess Part II [Official Video].mp3');
          newAudio.volume = 0.5;
          newAudio.currentTime = 1.7; // Start at 1.7 seconds for loop

          // Set up looping for the new instance
          setupSeamlessLoop(newAudio);

          newAudio.play().catch(error => {
          });

          // Replace the stored audio reference
          setGemAudio(newAudio);

          // Fade out and stop the old audio after a brief moment
          setTimeout(() => {
            audioInstance.pause();
          }, 150);
        }
      };

      audioInstance.addEventListener('timeupdate', handleTimeUpdate);
    };

    // Set up the seamless loop for the initial audio
    setupSeamlessLoop(audio);

    audio.play().catch(error => {
    });
  };

  // Handle celebration dismiss
  const handleCelebrationClick = () => {
    setShowCelebration(false);

    // Stop the gem discovery music
    if (gemAudio) {
      gemAudio.pause();
      gemAudio.currentTime = 0; // Reset to beginning
      setGemAudio(null);
    }

    // Remove gem from sandbox after a short delay
    setTimeout(() => {
      setGemHostPieceKey(null);
    }, 300);
  };

  const renderAllPieces = () => {
    const allPieces: Array<{ piece: PieceType, isLight: boolean, position: { top: string, left: string } }> = [];

    // Add light pieces
    allPieceTypes.forEach((piece, index) => {
      allPieces.push({
        piece,
        isLight: true,
        position: piecePositions[index]
      });
    });

    // Add dark pieces
    allPieceTypes.forEach((piece, index) => {
      allPieces.push({
        piece,
        isLight: false,
        position: piecePositions[index + 13]
      });
    });

    // Filter pieces based on active tab for mobile
    const filteredPieces = isMobile
      ? allPieces.filter(p => p.isLight === (activeColorTab === 'light'))
      : allPieces;

    const renderedPieces = filteredPieces.map(({ piece, isLight, position }, index) => {
      const suffix = isLight ? 'light' : 'dark';
      const pieceKey = `${piece}-${suffix}-${index}`;
      
      // Don't render pieces that have been placed on the board
      if (placedPieces.has(pieceKey)) {
        return null;
      }
      
      const isDragging = draggingPiece === pieceKey;
      const isSelected = isMobile && selectedPiece?.type === piece && selectedPiece?.isLight === isLight;
      const isOnBoard = isPieceOnBoard(piece, isLight);

      // Use dynamic position if available, otherwise use original position
      const currentPosition = dynamicPositions.get(pieceKey) || position;

      // Mobile tap handler
      const handleMobileTap = () => {
        if (!isMobile) return;
        if (isOnBoard) {
          return;
        }
        setSelectedPiece({ type: piece, isLight });
      };

      return (
        <img
          key={pieceKey}
          src={`/Icons/${piece}-${suffix}.png`}
          alt={`${piece}-${suffix}`}
          className={`${styles.floatingPiece} ${isSelected ? styles.selected : ''} ${isOnBoard ? styles.onBoard : ''}`}
          style={{
            position: isMobile ? 'static' : 'absolute',
            top: isMobile ? undefined : currentPosition.top,
            left: isMobile ? undefined : currentPosition.left,
            opacity: isDragging ? 0.3 : 1, // Semi-transparent when dragging for visual feedback
            pointerEvents: isDragging ? 'none' : 'auto',
            transform: isDragging ? 'scale(1.05)' : 'scale(1)',
            transition: isDragging ? 'none' : 'transform 0.2s ease',
            zIndex: isDragging ? 1001 : 10, // Pieces above gem
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          title={`${piece} (${isLight ? 'Light' : 'Dark'})`}
          draggable={!isMobile ? "true" : "false"}
          onDragStart={(e) => !isMobile && handleDragStart(e, piece, isLight, pieceKey, currentPosition)}
          onDragEnd={!isMobile ? handleDragEnd : undefined}
          onClick={handleMobileTap}
        />
      );
    }).filter(Boolean);

    // Add spacer element at the beginning for mobile to shift pieces right
    if (isMobile) {
      return [
        <div key="mobile-spacer" style={{ width: '45px', height: '45px' }} />,
        ...renderedPieces
      ];
    }

    return renderedPieces;
  };

  // Reset all pieces to their original tidy positions
  const tidyPieces = () => {
    setDynamicPositions(new Map());
    localStorage.removeItem('sandboxPiecePositions');
  };

  // Mark pieces that are already on the board (e.g. loaded from replay FEN)
  const markBoardPiecesAsPlaced = (pieces: Array<{type: PieceType, isLight: boolean}>) => {
    setPlacedPieces(prev => {
      const newSet = new Set(prev);
      pieces.forEach(({ type, isLight }) => {
        const suffix = isLight ? 'light' : 'dark';
        const typeIndex = allPieceTypes.indexOf(type);
        const index = isLight ? typeIndex : typeIndex + 13;
        const pieceKey = `${type}-${suffix}-${index}`;
        newSet.add(pieceKey);
      });
      return newSet;
    });
  };

  // Expose handler functions via imperative handle
  useImperativeHandle(ref, () => ({
    handlePiecePlaced,
    handlePieceDropFailed,
    returnCapturedPiece,
    isPieceOnBoard,
    getSelectedPiece: () => selectedPiece,
    clearSelectedPiece: () => setSelectedPiece(null),
    tidyPieces,
    markBoardPiecesAsPlaced,
    resetAllPlaced: () => setPlacedPieces(new Set()),
  }));


  return (
    <div
      className={styles.paletteForeground}
      onDrop={handleSandboxDrop}
      onDragOver={handleSandboxDragOver}
      onDragEnter={() => {}}
      onDragLeave={() => {}}
      style={{
        cursor: draggingPiece ? 'grabbing !important' : 'default'
      }}
    >
      {/* Color tabs for mobile */}
      {isMobile && (
        <div className={styles.colorTabs}>
          <button
            className={`${styles.colorTab} ${styles.white} ${activeColorTab === 'light' ? styles.active : ''}`}
            onClick={() => {
              setActiveColorTab('light');
              setSelectedPiece(null); // Clear selection when switching tabs
            }}
          >
            ○
          </button>
          <button
            className={`${styles.colorTab} ${styles.black} ${activeColorTab === 'dark' ? styles.active : ''}`}
            onClick={() => {
              setActiveColorTab('dark');
              setSelectedPiece(null); // Clear selection when switching tabs
            }}
          >
            ●
          </button>
        </div>
      )}

      {/* Independent hidden treasure - only show if not discovered */}
      {/* OBFUSCATION: Named as "terrain decoration" to hide from console inspection */}
      {/* CRITICAL: Gem position is FROZEN at initial placement - does NOT follow piece movements */}
      {gemHostPieceKey && !gemDiscovered && gemInitialPosition && (() => {
        // Don't render gem if the host piece has been placed on the board
        if (placedPieces.has(gemHostPieceKey)) return null;

        // Use the frozen initial position - gem stays in place even when piece moves
        const adjustedPosition = {
          top: `calc(${gemInitialPosition.top} + 1%)`, // Move slightly down
          left: `calc(${gemInitialPosition.left} + 0.5%)` // Move slightly right
        };

        return (
          <div
            data-terrain-type="sand-decoration-3"
            data-layer="background"
            role="presentation"
            aria-hidden="true"
            className={styles.hiddenGem}
            style={{
              position: 'absolute',
              top: adjustedPosition.top,
              left: adjustedPosition.left,
              zIndex: 0, // Below all pieces - truly hidden under them
              width: '40px',
              height: '40px',
              pointerEvents: 'auto', // Allow clicking to discover treasure
              transform: 'translate(0%, 0%)',
              opacity: 0.8,
              cursor: 'pointer', // Show it's clickable
              backgroundImage: 'url(/images/gemstone.png)',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
            onClick={handleGemClick}
          />
        );
      })()}
      
      {/* All pieces */}
      {renderAllPieces()}
      
      {/* Celebration overlay */}
      {showCelebration && (
        <div
          onClick={handleCelebrationClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '30px'
          }}
        >
          {/* Flying rainbow text around the screen */}
          {[
            { delay: '0s', duration: '4s' },
            { delay: '0.5s', duration: '3.5s' },
            { delay: '1s', duration: '4.5s' },
            { delay: '1.5s', duration: '3s' },
            { delay: '2s', duration: '5s' },
            { delay: '2.5s', duration: '3.8s' }
          ].map((timing, index) => (
            <div
              key={index}
              className={`${styles.flyingRainbowText}`}
              style={{
                position: 'absolute',
                fontSize: '32px',
                fontWeight: 'bold',
                pointerEvents: 'none',
                fontFamily: 'fantasy, serif',
                zIndex: 10000 + index,
                animationDelay: timing.delay
              }}
            >
              ✨ PRINCESS POWER UP!!! ✨
            </div>
          ))}
        
          {/* Gem in center with simple glow */}
          <img
            src="/images/gemstone.png"
            alt="Discovered Gem"
            className={styles.gemGlow}
            style={{
              width: '120px',
              height: '120px',
              filter: 'drop-shadow(0 0 40px #4FC3F7) drop-shadow(0 0 80px #81D4FA)'
            }}
          />
          
          {/* Celebration text - rainbow pulsating */}
          <div
            className={styles.rainbowText}
            style={{
              fontSize: '42px',
              fontWeight: 'bold',
              textAlign: 'center',
              fontFamily: 'fantasy, serif'
            }}
          >
            🎉 YOU FOUND A GEM! 🎉
          </div>

          {/* Princess sparkle levels message - rainbow pulsating */}
          <div
            className={styles.rainbowTextDelayed}
            style={{
              fontSize: '32px',
              fontWeight: 'bold',
              textAlign: 'center',
              fontFamily: 'fantasy, serif'
            }}
          >
            ✨ YOU GAINED 3 PRINCESS SPARKLE LEVELS! ✨
          </div>
          
          {/* Click instruction */}
          <div
            style={{
              color: '#ccc',
              fontSize: '18px',
              textAlign: 'center',
              marginTop: '20px'
            }}
          >
            Click anywhere to continue
          </div>
        </div>
      )}
    </div>
  );
});

export default PiecePalette;